// src/components/home/MotorsSection.jsx

import React from "react";
import {
  Check,
  ArrowRight,
  CarFront,
  Sparkles,
  BadgeCheck,
  RefreshCcw,
  SearchCheck,
} from "lucide-react";

import Button from "../common/Button";
import motorsImage from "../../assets/sell.png";

const items = [
  "Buy & Sell Used Cars",
  "Vehicle Exchange",
  "Vehicle Consultancy",
  "Vehicle Inspection",
  "Market Value Guidance",
  "Complete Support",
];

const MotorsSection = ({ onBookAppointment }) => {
  return (
    <section className="relative mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-20
          h-72
          w-72
          rounded-full
          bg-[#F97316]/10
          blur-[100px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-10
          h-80
          w-80
          rounded-full
          bg-[#00A260]/10
          blur-[110px]
        "
      />

      {/* =====================================================
          MAIN CARD
      ====================================================== */}

      <div
        className="
          group
          relative
          overflow-hidden
          rounded-[2rem]
          bg-[#061A3A]
          shadow-[0_25px_80px_rgba(6,26,58,0.18)]
        "
      >
        {/* Background decoration */}

        <div
          className="
            pointer-events-none
            absolute
            -right-40
            -top-40
            h-[500px]
            w-[500px]
            rounded-full
            bg-[#F97316]/10
            blur-[100px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -bottom-40
            -left-40
            h-[450px]
            w-[450px]
            rounded-full
            bg-[#00A260]/10
            blur-[100px]
          "
        />

        {/* =====================================================
            CONTENT GRID
        ====================================================== */}

        <div className="relative z-10 grid lg:grid-cols-[0.95fr_1.05fr]">
          {/* ===================================================
              LEFT CONTENT
          =================================================== */}

          <div
            className="
              relative
              flex
              flex-col
              justify-center
              p-6

              sm:p-9

              lg:p-12
              xl:p-14
            "
          >
            {/* Small label */}

            <div
              className="
                inline-flex
                w-fit
                items-center
                gap-2
                rounded-full
                border
                border-[#F97316]/25
                bg-[#F97316]/10
                px-3
                py-1.5
                text-[10px]
                font-bold
                uppercase
                tracking-[0.2em]
                text-[#FB923C]

                sm:px-4
                sm:py-2
                sm:text-[11px]
              "
            >
              <CarFront className="h-3.5 w-3.5" />

              NJV Motors
            </div>

            {/* Heading */}

            <h2
              className="
                mt-5
                max-w-xl
                text-[34px]
                font-extrabold
                leading-[1.02]
                tracking-tight
                text-white

                sm:text-4xl

                lg:text-[46px]

                xl:text-[52px]
              "
            >
              Your next vehicle
              <span className="block text-[#F97316]">
                starts here.
              </span>
            </h2>

            {/* Description */}

            <p
              className="
                mt-5
                max-w-xl
                text-sm
                leading-6
                text-white/60

                sm:text-base
                sm:leading-7
              "
            >
              Buy, sell or exchange your vehicle with professional
              guidance from NJV Motors. Get practical support from
              vehicle selection to final decision.
            </p>

            {/* =================================================
                QUICK HIGHLIGHTS
            ================================================== */}

            <div
              className="
                mt-7
                grid
                grid-cols-3
                gap-2

                sm:gap-3
              "
            >
              {/* Buy & Sell */}

              <div
                className="
                  rounded-2xl
                  border
                  border-white/10
                  bg-white/[0.045]
                  p-3
                  transition-all
                  duration-300
                  hover:border-[#F97316]/30
                  hover:bg-white/[0.07]

                  sm:p-4
                "
              >
                <BadgeCheck className="h-5 w-5 text-[#F97316]" />

                <p
                  className="
                    mt-3
                    text-[11px]
                    font-bold
                    text-white

                    sm:text-xs
                  "
                >
                  Buy & Sell
                </p>

                <p
                  className="
                    mt-1
                    text-[9px]
                    leading-4
                    text-white/40

                    sm:text-[10px]
                  "
                >
                  Used vehicles
                </p>
              </div>

              {/* Exchange */}

              <div
                className="
                  rounded-2xl
                  border
                  border-white/10
                  bg-white/[0.045]
                  p-3
                  transition-all
                  duration-300
                  hover:border-[#F97316]/30
                  hover:bg-white/[0.07]

                  sm:p-4
                "
              >
                <RefreshCcw className="h-5 w-5 text-[#F97316]" />

                <p
                  className="
                    mt-3
                    text-[11px]
                    font-bold
                    text-white

                    sm:text-xs
                  "
                >
                  Exchange
                </p>

                <p
                  className="
                    mt-1
                    text-[9px]
                    leading-4
                    text-white/40

                    sm:text-[10px]
                  "
                >
                  Upgrade easily
                </p>
              </div>

              {/* Consultancy */}

              <div
                className="
                  rounded-2xl
                  border
                  border-white/10
                  bg-white/[0.045]
                  p-3
                  transition-all
                  duration-300
                  hover:border-[#F97316]/30
                  hover:bg-white/[0.07]

                  sm:p-4
                "
              >
                <SearchCheck className="h-5 w-5 text-[#F97316]" />

                <p
                  className="
                    mt-3
                    text-[11px]
                    font-bold
                    text-white

                    sm:text-xs
                  "
                >
                  Consultancy
                </p>

                <p
                  className="
                    mt-1
                    text-[9px]
                    leading-4
                    text-white/40

                    sm:text-[10px]
                  "
                >
                  Expert guidance
                </p>
              </div>
            </div>

            {/* =================================================
                SERVICES
            ================================================== */}

            <div className="mt-7">
              <p
                className="
                  mb-3
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  text-white/35
                "
              >
                What NJV Motors provides
              </p>

              <div
                className="
                  grid
                  gap-x-6
                  gap-y-2.5

                  sm:grid-cols-2
                "
              >
                {items.map((item) => (
                  <div
                    key={item}
                    className="
                      flex
                      items-center
                      gap-2.5
                      text-xs
                      text-white/65
                      transition-colors
                      duration-300
                      hover:text-white

                      sm:text-sm
                    "
                  >
                    <span
                      className="
                        flex
                        h-5
                        w-5
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-[#F97316]/10
                      "
                    >
                      <Check className="h-3 w-3 text-[#F97316]" />
                    </span>

                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* =================================================
                CTA
            ================================================== */}

            <div
              className="
                mt-8
                flex
                flex-col
                gap-3

                sm:flex-row
                sm:items-center
              "
            >
              <Button
                variant="orange"
                size="lg"
                onClick={onBookAppointment}
                className="
                  group/motors
                  w-full
                  shadow-[0_12px_35px_rgba(249,115,22,0.20)]
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:shadow-[0_16px_40px_rgba(249,115,22,0.28)]

                  sm:w-auto
                "
              >
                Explore NJV Motors

                <ArrowRight
                  className="
                    h-4
                    w-4
                    transition-transform
                    duration-300
                    group-hover/motors:translate-x-1
                  "
                />
              </Button>

              <div
                className="
                  flex
                  items-center
                  justify-center
                  gap-2
                  text-[11px]
                  text-white/40

                  sm:justify-start
                "
              >
                <span
                  className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-[#F97316]
                    shadow-[0_0_10px_rgba(249,115,22,0.5)]
                  "
                />

                Professional vehicle support
              </div>
            </div>
          </div>

          {/* ===================================================
              RIGHT IMAGE
          =================================================== */}

          <div
            className="
              relative
              min-h-[300px]
              overflow-hidden

              sm:min-h-[400px]

              lg:min-h-[620px]
            "
          >
            {/* Image */}

            <img
              src={motorsImage}
              alt="NJV Motors vehicles"
              className="
                absolute
                inset-0
                h-full
                w-full
                object-cover
                transition-transform
                duration-[1200ms]
                group-hover:scale-[1.035]
              "
              onError={(e) => {
                e.currentTarget.src = "/images/placeholder.jpg";
              }}
            />

            {/* Dark left blend */}

            <div
              className="
                absolute
                inset-0
                bg-gradient-to-r
                from-[#061A3A]/80
                via-[#061A3A]/15
                to-transparent
              "
            />

            {/* Bottom blend */}

            <div
              className="
                absolute
                inset-x-0
                bottom-0
                h-1/2
                bg-gradient-to-t
                from-[#061A3A]/70
                to-transparent
              "
            />

            {/* Orange cinematic glow */}

            <div
              className="
                pointer-events-none
                absolute
                -right-20
                -top-20
                h-60
                w-60
                rounded-full
                bg-[#F97316]/20
                blur-[90px]
              "
            />

            {/* =================================================
                IMAGE TOP LABEL
            ================================================== */}

            <div
              className="
                absolute
                right-5
                top-5
                flex
                items-center
                gap-2
                rounded-full
                border
                border-white/15
                bg-black/25
                px-3
                py-2
                backdrop-blur-md

                sm:right-7
                sm:top-7
              "
            >
              <Sparkles className="h-3.5 w-3.5 text-[#F97316]" />

              <span
                className="
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.12em]
                  text-white/90

                  sm:text-[10px]
                "
              >
                NJV Motors
              </span>
            </div>

            {/* =================================================
                IMAGE BOTTOM CONTENT
            ================================================== */}

            <div
              className="
                absolute
                bottom-5
                left-5
                right-5

                sm:bottom-7
                sm:left-7
                sm:right-7
              "
            >
              {/* Orange line */}

              <div
                className="
                  mb-3
                  h-1
                  w-10
                  rounded-full
                  bg-[#F97316]
                "
              />

              <p
                className="
                  text-[10px]
                  font-bold
                  uppercase
                  tracking-[0.22em]
                  text-white/50
                "
              >
                Buy • Sell • Exchange
              </p>

              <h3
                className="
                  mt-1
                  text-xl
                  font-extrabold
                  tracking-tight
                  text-white

                  sm:text-2xl
                "
              >
                Find the right vehicle.
              </h3>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MotorsSection;